import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import orderBy from 'lodash/orderBy';

export const Index_GeographicFocusFragment = gql(/* GraphQL */ `
  fragment Index_GeographicFocusFragment on ForestryIndicators {
    geographicFocus {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type GeographicFocusProps = {
  className?: string;
  indicators: FragmentType<typeof Index_GeographicFocusFragment>;
};

export function GeographicFocus({ indicators, className }: GeographicFocusProps) {
  const { geographicFocus } = useFragment(Index_GeographicFocusFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('geographicFocus');

  const pieData = useMemo(() => {
    const colors = generateChartColors(geographicFocus.items.length);
    const sorted = orderBy(
      geographicFocus.items
        .filter((item) => !!item)
        .map((item) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [geographicFocus.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip className="ml-1">{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={geographicFocus.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="right" />
      </ChartCard.Body>
    </ChartCard>
  );
}
